import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';

interface CalendarShare {
    id: number;
    propertyId: number;
    name: string;
    token: string;
    calendarUrl: string;
    lastAccessed: string | null;
    created: string;
}

interface CalendarSharingProps {
    propertyId: number;
    disabled?: boolean;
}

const CalendarSharing = ({ propertyId, disabled }: CalendarSharingProps) => {
    const [shares, setShares] = useState<CalendarShare[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newShareName, setNewShareName] = useState('');
    const [creating, setCreating] = useState(false);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchShares = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiRawFetch(
                `api/properties/${propertyId}/calendar-shares`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch calendar shares');
            }

            const data = await response.json();
            setShares(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchShares();
    }, [fetchShares]);

    const handleCreate = async () => {
        if (!newShareName.trim()) {
            return;
        }

        try {
            setCreating(true);
            setError(null);

            const response = await apiRawFetch(
                `api/properties/${propertyId}/calendar-shares`,
                'POST',
                { name: newShareName.trim() }
            );

            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(data.error ?? 'Failed to create calendar share');
            }

            setDialogOpen(false);
            setNewShareName('');
            await fetchShares();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = async (share: CalendarShare) => {
        try {
            await navigator.clipboard.writeText(share.calendarUrl);
            setCopiedId(share.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            setError('Failed to copy to clipboard');
        }
    };

    const handleDelete = async (shareId: number) => {
        try {
            setDeleting(true);
            setError(null);

            const response = await apiRawFetch(
                `api/calendar-shares/${shareId}/delete`,
                'POST'
            );

            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(data.error ?? 'Failed to delete calendar share');
            }

            setDeleteConfirmId(null);
            await fetchShares();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#e5e7eb',
                    }}
                >
                    Calendar Sharing
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                    disabled={disabled}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid',
                        borderColor: 'secondary.main',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 2,
                        py: 0.5,
                    }}
                >
                    Create Link
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{
                        borderRadius: 0,
                        border: '3px solid #ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        fontWeight: 600,
                        mb: 2,
                        '& .MuiAlert-icon': {
                            color: '#ef4444',
                        },
                    }}
                >
                    {error}
                </Alert>
            )}

            {shares.length === 0 ? (
                <Box
                    sx={{
                        border: '2px solid #374151',
                        p: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase' }}
                    >
                        No calendar shares configured
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontSize: '0.75rem' }}
                    >
                        Create a share link to allow external calendar
                        subscriptions (Google, Apple, Outlook)
                    </Typography>
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow
                            sx={{
                                borderBottom: '2px solid #374151',
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 1,
                                }}
                            >
                                Name
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 1,
                                }}
                            >
                                Last Accessed
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 1,
                                    width: 160,
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shares.map(share => (
                            <TableRow
                                key={share.id}
                                sx={{
                                    borderBottom: '1px solid #374151',
                                }}
                            >
                                <TableCell sx={{ px: 1 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {share.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            fontSize: '0.65rem',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        Created{' '}
                                        {new Date(
                                            share.created
                                        ).toLocaleDateString()}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1 }}>
                                    <Typography variant="body2">
                                        {share.lastAccessed
                                            ? new Date(
                                                  share.lastAccessed
                                              ).toLocaleString()
                                            : 'Never'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1 }}>
                                    <Stack direction="row" spacing={1}>
                                        <Tooltip title="Copy calendar URL">
                                            <Button
                                                variant={
                                                    copiedId === share.id
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                color={
                                                    copiedId === share.id
                                                        ? 'success'
                                                        : 'primary'
                                                }
                                                size="small"
                                                onClick={() =>
                                                    handleCopy(share)
                                                }
                                                disabled={disabled}
                                                sx={{
                                                    borderRadius: 0,
                                                    borderWidth: '2px',
                                                    minWidth: 'auto',
                                                    px: 1,
                                                    '&:hover': {
                                                        borderWidth: '2px',
                                                    },
                                                }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Revoke this share link">
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() =>
                                                    setDeleteConfirmId(share.id)
                                                }
                                                disabled={disabled}
                                                sx={{
                                                    borderRadius: 0,
                                                    borderWidth: '2px',
                                                    minWidth: 'auto',
                                                    px: 1,
                                                    '&:hover': {
                                                        borderWidth: '2px',
                                                        backgroundColor:
                                                            'rgba(239, 68, 68, 0.1)',
                                                    },
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </Button>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Create Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => !creating && setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                    }}
                >
                    Create Calendar Share
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Create a named share link for this property&apos;s
                            calendar. Each link can be individually revoked.
                        </Typography>
                        <TextField
                            label="Name"
                            placeholder="e.g., Google Calendar, Cleaning Team"
                            fullWidth
                            value={newShareName}
                            onChange={e => setNewShareName(e.target.value)}
                            disabled={creating}
                            autoFocus
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                    '& input': {
                                        backgroundColor: '#0f0f23',
                                    },
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#374151',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '3px',
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '1rem',
                                    letterSpacing: '0.05em',
                                },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 2,
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setDialogOpen(false)}
                        disabled={creating}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={creating || !newShareName.trim()}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {creating ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmId !== null}
                onClose={() => !deleting && setDeleteConfirmId(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #ef4444',
                        backgroundColor: '#1a1a2e',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #ef4444',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                    }}
                >
                    Revoke Calendar Share
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius: 0,
                                border: '3px solid #f59e0b',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#f59e0b',
                                },
                            }}
                        >
                            This will immediately invalidate the calendar
                            subscription. Anyone using this link will no longer
                            receive updates.
                        </Alert>
                        <Typography variant="body2">
                            Are you sure you want to revoke &quot;
                            {shares.find(s => s.id === deleteConfirmId)?.name}
                            &quot;?
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 2,
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setDeleteConfirmId(null)}
                        disabled={deleting}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            deleteConfirmId && handleDelete(deleteConfirmId)
                        }
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid',
                            borderColor: 'error.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {deleting ? 'Revoking...' : 'Revoke'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CalendarSharing;
